import { LOCAL_STORAGE } from '@/constants';

// Whether onboarding is done lives on the account (UserResourceDismiss.ONBOARDING), because a mark in
// localStorage is per-browser and used to hide the onboarding from every account that registered later
// in the same browser. What stays here is the ICP step's own flag: it only decides which step the
// dialog opens on, so it is not worth a round trip.
const onboardingKey = (base: string, userPublicId?: number | string) => `${base}:${userPublicId ?? 'anon'}`;

export const isIcpFilled = (userPublicId?: number | string) => {
    try {
        return Boolean(
            localStorage.getItem(onboardingKey(LOCAL_STORAGE.ONBOARDING_ICP_FILLED, userPublicId)),
        );
    } catch {
        return false;
    }
};

export const markIcpFilled = (userPublicId?: number | string) => {
    try {
        localStorage.setItem(onboardingKey(LOCAL_STORAGE.ONBOARDING_ICP_FILLED, userPublicId), '1');
    } catch {
        // Ignore storage errors (e.g. blocked/disabled localStorage)
    }
};

// Day the mark moved onto the account. Browser marks written before it carry no owner, so the only
// thing we know is that one can't belong to an account that didn't exist yet: handing it to whoever
// reads it first is what hid the onboarding from accounts registered later. The workspace date stands
// in for the account's, which is exact for the only people who see onboarding: one workspace, no
// profiles. Set it to the day this reaches production: too early only costs those accounts one more
// pass through the flow, too late lets new accounts claim someone else's mark.
const ACCOUNT_MARK_SHIPPED_AT = new Date('2026-09-01T00:00:00Z').getTime();

// True when this browser still carries a "finished" mark this account can claim: its own scoped mark,
// or an unscoped one written back when the account already existed. Callers move it onto the account.
export const hasBrowserOnboardingMark = (
    userPublicId?: number | string,
    workspaceCreatedAt?: string | number | Date | null,
) => {
    try {
        if (localStorage.getItem(onboardingKey(LOCAL_STORAGE.ONBOARDING_FINISHED, userPublicId))) {
            return true;
        }
        const createdAt = workspaceCreatedAt == null ? NaN : new Date(workspaceCreatedAt).getTime();
        return (
            createdAt < ACCOUNT_MARK_SHIPPED_AT &&
            Boolean(localStorage.getItem(LOCAL_STORAGE.ONBOARDING_FINISHED))
        );
    } catch {
        return false;
    }
};
